import { useContext, useEffect, useMemo } from 'react';

import { WeaponPlaceOfResidenceSchema } from '@/features/ui';

import { UkrainianAddressSchema } from '@/shared/ui/AddressBlock/model';

import { allowedForSelectionDocumentTypesForDeclaredPlaceOfResidence, IdConstants } from '@/components/Forms/constants';

import { useAsyncDataLoader } from '@/hooks/useAsyncDataLoader';

import { getERZDocumentTypeEntites } from '@/modules/erz/helpers';

import AuthContext from '@/contexts/Auth/AuthContext';

import { DocumentTypeCode } from '@/api/types';

interface UsePlaceOfResidenceDefaultsResult {
  documentTypeEntities: Array<{
    id: string;
    label: string;
    code: DocumentTypeCode;
  }>;
  isDocumentTypesLoading: boolean;
  registrationFormAddress: Partial<WeaponPlaceOfResidenceSchema['registeredNewAddress']>;
  isUkraine: boolean;
}

export const usePlaceOfResidenceDefaults = (): UsePlaceOfResidenceDefaultsResult => {
  const {
    addressesData: { REGISTRATION },
  } = useContext(AuthContext);
  const {
    data: documentTypeEntitiesResponse = [],
    load: loadDocumentTypeEntities,
    isLoading: isDocumentTypesLoading,
  } = useAsyncDataLoader(() =>
    getERZDocumentTypeEntites([
      ...allowedForSelectionDocumentTypesForDeclaredPlaceOfResidence,
      DocumentTypeCode.VITYAG_TERITORIALNOYI_GROMADI,
    ]),
  );

  useEffect(() => {
    loadDocumentTypeEntities();
  }, []);

  const registrationFormAddress = useMemo<Partial<UkrainianAddressSchema>>(
    () =>
      ({
        country: REGISTRATION.country,
        region: REGISTRATION.region,
        district: REGISTRATION.district,
        settlement: REGISTRATION.settlement,
        street: REGISTRATION.street,
        house: REGISTRATION.house,
        building: REGISTRATION.building,
        apartment: REGISTRATION.apartment,
        zip_code: null,
      }) as Partial<UkrainianAddressSchema>,
    [REGISTRATION],
  );

  const isUkraine = registrationFormAddress.country?.id === IdConstants.UKRAINE;

  const documentTypeEntities = useMemo(() => {
    return (documentTypeEntitiesResponse ?? []).reduce<
      Array<{
        id: string;
        label: string;
        code: DocumentTypeCode;
      }>
    >((acc, entity) => {
      if (entity?.id && entity?.label && entity?.code && entity.code !== 'null') {
        acc.push({ id: entity.id, label: entity.label, code: entity.code });
      }
      return acc;
    }, []);
  }, [documentTypeEntitiesResponse]);

  return {
    documentTypeEntities,
    isDocumentTypesLoading,
    registrationFormAddress,
    isUkraine,
  };
};
